/**
 * Booking date rules - the client-side mirror of BookingUtils.js's
 * checkLeadTime() + checkDuration() (common-core). The server is the
 * authority; this module only makes sure every range a picker can produce is
 * one the server will accept.
 *
 * CANONICAL COPY: Customer/customer/src/helpers/dateRange.js. Identical copies
 * live at Admin/admin_frontend/src/Helpers/dateRange.js and
 * Customer/customer_frontend/src/Helpers/dateRange.js (no shared package in
 * this monorepo) - change all three together, byte for byte.
 *
 * 1. LEAD TIME. The current time is first rounded UP to the next whole hour,
 *    then the trip may start only after `leadHours` more (rounded up again to
 *    a whole hour if leadHours is fractional). Always at least as strict as
 *    the server, which counts exact hours from the moment the request lands.
 *        now 02:20, lead 3 -> round up to 03:00 -> earliest start 06:00
 * 2. DURATION & HORIZON. A trip lasts at least `minTripHours`, at most
 *    `maxTripMonths` calendar months, and may start no later than 23:00 on the
 *    day `maxHorizonMonths` calendar months from today.
 * 3. WHOLE HOURS. Every date this module produces sits exactly on an hour.
 * 4. IST. Every calendar operation (days, months, "which hour is this")
 *    happens on the Asia/Kolkata clock, whatever timezone the browser is in -
 *    the same calendar the server uses, so "3 months from 1 Mar" is 1 Jun on
 *    both sides. India has no DST, so a fixed +05:30 offset is exact.
 *
 * Use createDateRules(rules) to get the rule functions bound to one portal's
 * settings; rulesFromSettings() turns a settings payload into those rules.
 * Every function is pure. Nothing here touches window, document or React.
 */

const HOUR_MS = 60 * 60 * 1000;
export const BOOKING_TZ_OFFSET_MS = 330 * 60 * 1000;

// Same fallbacks BookingUtils.js uses when a setting is unset.
export const DEFAULT_RULES = Object.freeze({
  leadHours: 3,
  minTripHours: 6,
  maxHorizonMonths: 3,
  maxTripMonths: 3,
});

/* ------------------------------------------------------------------ */
/* primitives (IST wall clock)                                         */
/* ------------------------------------------------------------------ */

// A Date whose UTC fields read as the IST wall clock, and back.
const toWall = (value) => new Date(new Date(value).getTime() + BOOKING_TZ_OFFSET_MS);
const fromWall = (wall) => new Date(wall.getTime() - BOOKING_TZ_OFFSET_MS);

export const isValidDate = (value) =>
  value instanceof Date && !Number.isNaN(value.getTime());

/** Accepts a Date, an epoch-ms number, or a numeric string. Returns null for anything else. */
export const toDate = (value) => {
  if (value instanceof Date) return isValidDate(value) ? new Date(value.getTime()) : null;
  if (value === null || value === undefined || value === "") return null;
  const ms = Number(value);
  if (!Number.isFinite(ms)) return null;
  const date = new Date(ms);
  return isValidDate(date) ? date : null;
};

/** IST calendar fields of a moment. month is 0-11, weekday 0 (Sun) - 6. */
export const partsOf = (value) => {
  const wall = toWall(value);
  return {
    year: wall.getUTCFullYear(),
    month: wall.getUTCMonth(),
    day: wall.getUTCDate(),
    hour: wall.getUTCHours(),
    minute: wall.getUTCMinutes(),
    weekday: wall.getUTCDay(),
  };
};

/** The IST hour (0-23) of a moment. */
export const hourOf = (value) => partsOf(value).hour;

/** Midnight IST on the given calendar day (month 0-11) - use this for calendar cells. */
export const istDay = (year, month, day) => fromWall(new Date(Date.UTC(year, month, day)));

/** Rule 3 - drop minutes/seconds/ms on the IST clock. */
export const floorToHour = (value) => {
  const wall = toWall(value);
  wall.setUTCMinutes(0, 0, 0);
  return fromWall(wall);
};

/** Rule 1, first half - 02:00 and 02:59 both round up to 03:00. */
export const ceilToNextHour = (value) => new Date(floorToHour(value).getTime() + HOUR_MS);

const ceilToWholeHour = (value) => {
  const floored = floorToHour(value);
  return floored.getTime() < new Date(value).getTime() ? new Date(floored.getTime() + HOUR_MS) : floored;
};

export const addHours = (value, hours) => new Date(new Date(value).getTime() + hours * HOUR_MS);

export const startOfDay = (value) => {
  const wall = toWall(value);
  wall.setUTCHours(0, 0, 0, 0);
  return fromWall(wall);
};

export const startOfMonth = (value) => {
  const { year, month } = partsOf(value);
  return istDay(year, month, 1);
};

export const daysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

export const isSameDay = (a, b) =>
  isValidDate(a) && isValidDate(b) && startOfDay(a).getTime() === startOfDay(b).getTime();

/** Month arithmetic without month-end overflow (30 Nov + 3 = 28 Feb, not 2 Mar) - same as BookingUtils.js. */
export const addMonths = (value, months) => {
  const wall = toWall(value);
  const day = wall.getUTCDate();
  wall.setUTCDate(1);
  wall.setUTCMonth(wall.getUTCMonth() + months);
  wall.setUTCDate(Math.min(day, daysInMonth(wall.getUTCFullYear(), wall.getUTCMonth())));
  return fromWall(wall);
};

/** A moment on `day` at `hour` o'clock IST sharp. */
export const withHour = (day, hour) => new Date(startOfDay(day).getTime() + hour * HOUR_MS);

/* ------------------------------------------------------------------ */
/* settings -> rules                                                   */
/* ------------------------------------------------------------------ */

const nonNegative = (value, fallback) => (Number.isFinite(Number(value)) && Number(value) >= 0 && value !== null && value !== "" ? Number(value) : fallback);
const positive = (value, fallback) => (Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : fallback);

/**
 * Normalize a settings payload into rules for one portal. `channel` is
 * "admin" | "customer" | "staff", mirroring BookingUtils.js's own param - it
 * only picks which lead time applies (duration is shared by every portal).
 * `leadTime` may be a number (what each portal's endpoint already returns
 * for itself) or a per-channel object ({ admin, customer, staff }).
 */
export const rulesFromSettings = ({ leadTime, duration } = {}, channel = "customer") => {
  const lead = leadTime !== null && typeof leadTime === "object" ? leadTime[channel] : leadTime;
  return {
    leadHours: nonNegative(lead, DEFAULT_RULES.leadHours),
    minTripHours: positive(duration?.minHours ?? duration?.minTripHours, DEFAULT_RULES.minTripHours),
    maxHorizonMonths: positive(duration?.maxHorizonMonths, DEFAULT_RULES.maxHorizonMonths),
    maxTripMonths: positive(duration?.maxTripMonths, DEFAULT_RULES.maxTripMonths),
  };
};

/* ------------------------------------------------------------------ */
/* the rules, bound to one portal's settings                           */
/* ------------------------------------------------------------------ */

export const createDateRules = (input = DEFAULT_RULES) => {
  const rules = Object.freeze({
    leadHours: nonNegative(input.leadHours, DEFAULT_RULES.leadHours),
    minTripHours: Math.ceil(positive(input.minTripHours, DEFAULT_RULES.minTripHours)),
    maxHorizonMonths: positive(input.maxHorizonMonths, DEFAULT_RULES.maxHorizonMonths),
    maxTripMonths: positive(input.maxTripMonths, DEFAULT_RULES.maxTripMonths),
  });

  /** Rule 1 - the earliest pickup that may be chosen. */
  const earliestStart = (now = new Date()) =>
    ceilToWholeHour(addHours(ceilToNextHour(now), rules.leadHours));

  /** Rule 2 - the latest pickup that may be chosen (23:00 IST on the horizon day). */
  const latestStart = (now = new Date()) =>
    withHour(addMonths(startOfDay(now), rules.maxHorizonMonths), 23);

  /** Rule 2 - the earliest legal drop-off for a given pickup. */
  const minEndFor = (start) => addHours(floorToHour(start), rules.minTripHours);

  /** Rule 2 - the latest legal drop-off for a given pickup. */
  const maxEndFor = (start) => addMonths(floorToHour(start), rules.maxTripMonths);

  /** Whole hours between pickup and drop-off. */
  const tripHours = (range) =>
    !range?.start || !range?.end ? 0 : Math.round((range.end.getTime() - range.start.getTime()) / HOUR_MS);

  /* ---------------- calendar queries ---------------- */

  /** Can this day hold a legal pickup? (Today drops out once the lead time rolls past midnight.) */
  const isDaySelectable = (day, now = new Date()) => {
    const value = startOfDay(day).getTime();
    return (
      value >= startOfDay(earliestStart(now)).getTime() &&
      value <= startOfDay(latestStart(now)).getTime()
    );
  };

  /** IST hours that may be picked for the pickup on a given day. */
  const startHourRange = (day, now = new Date()) => {
    const floor = earliestStart(now);
    const ceiling = latestStart(now);
    const min = isSameDay(day, floor) ? hourOf(floor) : 0;
    const max = isSameDay(day, ceiling) ? hourOf(ceiling) : 23;
    return { min, max: Math.max(min, max) };
  };

  /** IST hours that may be picked for the drop-off on a given day, given the pickup. */
  const endHourRange = (day, start) => {
    const floor = minEndFor(start);
    const ceiling = maxEndFor(start);
    const min = isSameDay(day, floor) ? hourOf(floor) : 0;
    const max = isSameDay(day, ceiling) ? hourOf(ceiling) : 23;
    return { min, max: Math.max(min, max) };
  };

  /** Can this day hold a legal drop-off for the given pickup? */
  const canHostEnd = (day, start) => {
    const value = startOfDay(day).getTime();
    return (
      value >= startOfDay(minEndFor(start)).getTime() &&
      value <= startOfDay(maxEndFor(start)).getTime()
    );
  };

  /* ---------------- range operations - never return a partial or illegal range ---------------- */

  const clampStart = (start, now = new Date()) => {
    const min = earliestStart(now);
    const max = latestStart(now);
    let value = floorToHour(start);
    if (value.getTime() < min.getTime()) value = min;
    if (value.getTime() > max.getTime()) value = max;
    return value;
  };

  /** Force any {start, end} pair - partial, stale or malformed - into a legal range. */
  const clampRange = (range, now = new Date()) => {
    const rawStart = toDate(range?.start);
    const start = clampStart(rawStart || earliestStart(now), now);
    const minEnd = minEndFor(start);
    const maxEnd = maxEndFor(start);
    const rawEnd = toDate(range?.end);
    let end = rawEnd ? floorToHour(rawEnd) : minEnd;
    if (end.getTime() < minEnd.getTime()) end = minEnd;
    if (end.getTime() > maxEnd.getTime()) end = maxEnd;
    return { start, end };
  };

  const defaultRange = (now = new Date()) => {
    const start = earliestStart(now);
    return { start, end: minEndFor(start) };
  };

  /**
   * One click on a calendar day.
   * `phase` is "start" when the next click begins a new range, "end" when it
   * chooses the drop-off. Always returns a complete range - never a null end.
   */
  const applyDayClick = ({ day, range, phase = "start", now = new Date() }) => {
    const current = clampRange(range, now);
    const clicked = startOfDay(day);

    const startsNewRange = phase === "start" || clicked.getTime() < startOfDay(current.start).getTime();

    if (startsNewRange) {
      const bounds = startHourRange(clicked, now);
      const hour = Math.min(bounds.max, Math.max(bounds.min, hourOf(current.start)));
      const start = clampStart(withHour(clicked, hour), now);
      return { range: clampRange({ start, end: current.end }, now), phase: "end" };
    }

    const start = current.start;
    const minEnd = minEndFor(start);

    if (!canHostEnd(clicked, start)) {
      const fallback = clicked.getTime() < startOfDay(minEnd).getTime() ? minEnd : maxEndFor(start);
      return { range: { start, end: fallback }, phase: "start" };
    }

    const bounds = endHourRange(clicked, start);
    const hour = Math.min(bounds.max, Math.max(bounds.min, hourOf(current.end)));
    return { range: { start, end: withHour(clicked, hour) }, phase: "start" };
  };

  const setStartHour = (hour, range, now = new Date()) => {
    const current = clampRange(range, now);
    const start = clampStart(withHour(current.start, hour), now);
    return clampRange({ start, end: current.end }, now);
  };

  const setEndHour = (hour, range, now = new Date()) => {
    const current = clampRange(range, now);
    return clampRange({ start: current.start, end: withHour(current.end, hour) }, now);
  };

  /* ---------------- URL round-trip ---------------- */

  /**
   * Read a range out of URL parameters. Anything missing, malformed, in the
   * past or outside the rules is clamped into the nearest legal range - never
   * rejected - and `adjusted` reports it so the caller can rewrite the URL.
   */
  const parseRange = (startParam, endParam, now = new Date()) => {
    const rawStart = toDate(startParam);
    const rawEnd = toDate(endParam);
    const range = clampRange({ start: rawStart, end: rawEnd }, now);
    const adjusted =
      !rawStart ||
      !rawEnd ||
      range.start.getTime() !== rawStart.getTime() ||
      range.end.getTime() !== rawEnd.getTime();
    return { start: range.start, end: range.end, adjusted };
  };

  /** Always returns epoch-ms strings for a legal range - never "null" or "NaN". */
  const rangeToParams = (range, now = new Date()) => {
    const safe = clampRange(range, now);
    return { startDate: String(safe.start.getTime()), endDate: String(safe.end.getTime()) };
  };

  return Object.freeze({
    rules,
    earliestStart,
    latestStart,
    minEndFor,
    maxEndFor,
    tripHours,
    isDaySelectable,
    startHourRange,
    endHourRange,
    canHostEnd,
    clampStart,
    clampRange,
    defaultRange,
    applyDayClick,
    setStartHour,
    setEndHour,
    parseRange,
    rangeToParams,
  });
};

/* ------------------------------------------------------------------ */
/* display                                                             */
/* ------------------------------------------------------------------ */

/** "DD/MM/YYYY h:mm am" on the IST clock - same shape the old project got from moment. */
export const formatRangeDate = (value) => {
  const date = toDate(value);
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const { year, month, day, hour, minute } = partsOf(date);
  const ampm = hour >= 12 ? "pm" : "am";
  return `${pad(day)}/${pad(month + 1)}/${year} ${hour % 12 || 12}:${pad(minute)} ${ampm}`;
};

/** "12 AM" ... "11 PM" for an IST hour 0-23. */
export const hourLabel = (hour) => `${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`;
